/**
 * Score Input Component
 *
 * Visual score input for 1-5 scale used in evaluations.
 * Includes tooltips with score criteria descriptions on hover.
 */

import { useState, useRef, useEffect } from 'react';
import { useScoreCriteria } from '../../settings/useSettings';
import type { ScoreCriteriaDTO } from '@functieloonhuis/shared';

export const SCORE_LABELS: Record<number, string> = {
  1: 'Onvoldoende',
  2: 'Matig',
  3: 'Voldoende',
  4: 'Goed',
  5: 'Uitzonderlijk',
};

// Percentage thresholds for each score level (matching backend scoreService)
// Uses min-only check to properly handle decimal values (e.g., 69.4 should be Voldoende, not fall through)
const SCORE_THRESHOLDS = [
  { level: 5, label: 'Uitzonderlijk', min: 85 },
  { level: 4, label: 'Goed', min: 70 },
  { level: 3, label: 'Voldoende', min: 55 },
  { level: 2, label: 'Matig', min: 40 },
  { level: 1, label: 'Onvoldoende', min: 0 },
];

/**
 * Convert a percentage (0-100) to a score level (1-5)
 * Checks from highest to lowest threshold to handle decimal values correctly
 */
export function percentageToScoreLevel(percentage: number): number {
  const clamped = Math.max(0, Math.min(100, percentage));
  for (const threshold of SCORE_THRESHOLDS) {
    if (clamped >= threshold.min) {
      return threshold.level;
    }
  }
  return 1; // fallback
}

const SCORE_COLORS: Record<number, string> = {
  1: 'bg-red-50 border-red-200 text-red-400 hover:bg-red-100 hover:text-red-600',
  2: 'bg-orange-50 border-orange-200 text-orange-400 hover:bg-orange-100 hover:text-orange-600',
  3: 'bg-yellow-50 border-yellow-200 text-yellow-500 hover:bg-yellow-100 hover:text-yellow-600',
  4: 'bg-lime-50 border-lime-200 text-lime-400 hover:bg-lime-100 hover:text-lime-600',
  5: 'bg-green-50 border-green-200 text-green-400 hover:bg-green-100 hover:text-green-600',
};

const SELECTED_COLORS: Record<number, string> = {
  1: 'bg-red-600 border-red-600 text-white',
  2: 'bg-orange-600 border-orange-600 text-white',
  3: 'bg-yellow-600 border-yellow-600 text-white',
  4: 'bg-lime-600 border-lime-600 text-white',
  5: 'bg-green-600 border-green-600 text-white',
};


interface ScoreTooltipProps {
  score: number;
  criteria: ScoreCriteriaDTO | undefined;
  isVisible: boolean;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
}

function ScoreTooltip({ score, criteria, isVisible, buttonRef }: ScoreTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (isVisible && buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      // Position tooltip above the button, centered
      let left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
      const top = buttonRect.top - tooltipRect.height - 8;

      // Ensure tooltip stays within viewport
      const viewportWidth = window.innerWidth;
      const padding = 8;

      if (left < padding) {
        left = padding;
      } else if (left + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - tooltipRect.width - padding;
      }

      setPosition({ top, left });
    }
  }, [isVisible, buttonRef]);

  if (!isVisible) return null;

  const label = criteria?.label || SCORE_LABELS[score];
  const description = criteria?.description;

  return (
    <div
      ref={tooltipRef}
      className="fixed z-50 max-w-xs px-3 py-2 text-sm bg-gray-800 text-white rounded-lg shadow-lg"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}
    >
      <div className="font-semibold mb-1">
        {score} - {label}
      </div>
      {description && (
        <div className="text-gray-200 text-xs leading-relaxed">
          {description}
        </div>
      )}
      {!description && (
        <div className="text-gray-400 text-xs italic">
          Geen criteria beschrijving beschikbaar
        </div>
      )}
      {/* Arrow pointing down */}
      <div
        className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-gray-800"
      />
    </div>
  );
}

interface ScoreInputProps {
  value: number | null;
  onChange: (score: number) => void;
  showLabels?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  competencyId?: number; // Optional: for competency-specific criteria
  taskId?: number; // Optional: for task-specific criteria
}

export function ScoreInput({
  value,
  onChange,
  showLabels = true,
  disabled = false,
  size = 'md',
  competencyId,
  taskId,
}: ScoreInputProps) {
  const [hoveredScore, setHoveredScore] = useState<number | null>(null);
  const buttonRefs = useRef<Map<number, HTMLButtonElement | null>>(new Map());

  // Determine context type and ID for score criteria
  // Priority: taskId > competencyId > general
  const contextType = taskId ? 'task' : competencyId ? 'competency' : 'general';
  const contextId = taskId || competencyId;

  // Fetch score criteria based on context (task-specific, competency-specific, or general)
  const { data: criteria } = useScoreCriteria(contextType, contextId);

  const getCriteriaForScore = (score: number): ScoreCriteriaDTO | undefined => {
    return criteria?.find((c) => c.scoreLevel === score);
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  return (
    <div className="flex gap-2 flex-wrap">
      {[1, 2, 3, 4, 5].map((score) => {
        const isSelected = value === score;
        const criteriaItem = getCriteriaForScore(score);

        return (
          <div key={score} className="relative">
            <button
              ref={(el) => { buttonRefs.current.set(score, el); }}
              type="button"
              onClick={() => onChange(score)}
              disabled={disabled}
              onMouseEnter={() => setHoveredScore(score)}
              onMouseLeave={() => setHoveredScore(null)}
              className={`
                ${sizeClasses[size]}
                rounded-lg border-2 font-medium transition-colors
                ${isSelected
                  ? SELECTED_COLORS[score]
                  : SCORE_COLORS[score]
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <span className="font-bold">{score}</span>
              {showLabels && (
                <span className="hidden sm:inline ml-1 text-xs font-normal">
                  {criteriaItem?.label || SCORE_LABELS[score]}
                </span>
              )}
            </button>
            <ScoreTooltip
              score={score}
              criteria={criteriaItem}
              isVisible={hoveredScore === score}
              buttonRef={{ current: buttonRefs.current.get(score) || null }}
            />
          </div>
        );
      })}
    </div>
  );
}

interface ScoreDisplayProps {
  score: number | null;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function ScoreDisplay({ score, showLabel = true, size = 'md' }: ScoreDisplayProps) {
  if (score === null) {
    return (
      <span className="text-gray-400 text-sm italic">Niet beoordeeld</span>
    );
  }

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-medium
        ${sizeClasses[size]}
        ${SELECTED_COLORS[score]}
      `}
    >
      <span className="font-bold">{score}</span>
      {showLabel && (
        <span className="hidden sm:inline font-normal">
          {SCORE_LABELS[score]}
        </span>
      )}
    </span>
  );
}
